import React, { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Slider } from './ui/slider';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  MessageCircle, X, Send, Minimize2, Maximize2, Sparkles, User,
  Loader2, Heart, Activity, Moon, Droplets, Scale, Smile, Zap,
  PlusCircle, CheckCircle2
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getSessionId = () => {
  let sessionId = localStorage.getItem('annalise_session_id');
  if (!sessionId) {
    sessionId = `annalise_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('annalise_session_id', sessionId);
  }
  return sessionId;
};

const isNewVisitor = () => {
  const visitCount = parseInt(localStorage.getItem('visit_count') || '0');
  localStorage.setItem('visit_count', (visitCount + 1).toString());
  return visitCount === 0;
};

// Health Data Entry Form
const HealthDataForm = ({ onSubmit, onClose }) => {
  const [formData, setFormData] = useState({
    weight: '', heart_rate: '', blood_pressure_systolic: '', blood_pressure_diastolic: '',
    sleep_hours: '', sleep_quality: '', steps: '', water_glasses: '', exercise_minutes: '',
    mood: '', stress_level: 5, energy_level: 5, notes: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    const dataToSend = {
      user_session: getSessionId(),
      weight: formData.weight ? parseFloat(formData.weight) : null,
      heart_rate: formData.heart_rate ? parseInt(formData.heart_rate) : null,
      blood_pressure_systolic: formData.blood_pressure_systolic ? parseInt(formData.blood_pressure_systolic) : null,
      blood_pressure_diastolic: formData.blood_pressure_diastolic ? parseInt(formData.blood_pressure_diastolic) : null,
      sleep_hours: formData.sleep_hours ? parseFloat(formData.sleep_hours) : null,
      sleep_quality: formData.sleep_quality || null,
      steps: formData.steps ? parseInt(formData.steps) : null,
      water_glasses: formData.water_glasses ? parseInt(formData.water_glasses) : null,
      exercise_minutes: formData.exercise_minutes ? parseInt(formData.exercise_minutes) : null,
      mood: formData.mood || null,
      stress_level: formData.stress_level,
      energy_level: formData.energy_level,
      notes: formData.notes || null
    };
    try {
      await axios.post(`${API}/health-data/quick-entry`, dataToSend);
      setSubmitted(true);
      // Dispatch event so Health Dashboard refreshes immediately
      window.dispatchEvent(new CustomEvent('healthDataUpdated'));
      setTimeout(() => { onSubmit(dataToSend); onClose(); }, 1500);
    } catch (error) {
      console.error('Error submitting health data:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="p-6 text-center" data-testid="health-form-success">
        <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Data Recorded!</h3>
        <p className="text-sm text-gray-600">Your health data has been saved successfully.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="p-4 space-y-4 max-h-[400px] overflow-y-auto" data-testid="health-data-form">
      <div className="text-center mb-4">
        <h3 className="font-semibold text-gray-900">Log Health Data</h3>
        <p className="text-xs text-gray-500">Fill in what you have - all fields are optional</p>
      </div>

      {/* Vitals */}
      <div className="space-y-3">
        <p className="text-xs font-medium text-gray-500 uppercase tracking-wider flex items-center gap-1">
          <Heart className="w-3 h-3" /> Vitals
        </p>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-xs">Heart Rate (bpm)</Label>
            <Input type="number" placeholder="72" value={formData.heart_rate}
              onChange={(e) => setFormData({...formData, heart_rate: e.target.value})} className="h-8 text-sm" data-testid="input-heart-rate" />
          </div>
          <div>
            <Label className="text-xs">Weight (lbs)</Label>
            <Input type="number" placeholder="165" value={formData.weight}
              onChange={(e) => setFormData({...formData, weight: e.target.value})} className="h-8 text-sm" data-testid="input-weight" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-xs">BP Systolic</Label>
            <Input type="number" placeholder="120" value={formData.blood_pressure_systolic}
              onChange={(e) => setFormData({...formData, blood_pressure_systolic: e.target.value})} className="h-8 text-sm" />
          </div>
          <div>
            <Label className="text-xs">BP Diastolic</Label>
            <Input type="number" placeholder="80" value={formData.blood_pressure_diastolic}
              onChange={(e) => setFormData({...formData, blood_pressure_diastolic: e.target.value})} className="h-8 text-sm" />
          </div>
        </div>
      </div>

      {/* Lifestyle */}
      <div className="space-y-3">
        <p className="text-xs font-medium text-gray-500 uppercase tracking-wider flex items-center gap-1">
          <Activity className="w-3 h-3" /> Lifestyle
        </p>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-xs">Sleep (hours)</Label>
            <Input type="number" step="0.5" placeholder="7.5" value={formData.sleep_hours}
              onChange={(e) => setFormData({...formData, sleep_hours: e.target.value})} className="h-8 text-sm" data-testid="input-sleep" />
          </div>
          <div>
            <Label className="text-xs">Sleep Quality</Label>
            <Select value={formData.sleep_quality} onValueChange={(v) => setFormData({...formData, sleep_quality: v})}>
              <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="poor">Poor</SelectItem>
                <SelectItem value="fair">Fair</SelectItem>
                <SelectItem value="good">Good</SelectItem>
                <SelectItem value="excellent">Excellent</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div>
            <Label className="text-xs">Steps</Label>
            <Input type="number" placeholder="8000" value={formData.steps}
              onChange={(e) => setFormData({...formData, steps: e.target.value})} className="h-8 text-sm" data-testid="input-steps" />
          </div>
          <div>
            <Label className="text-xs">Water (glasses)</Label>
            <Input type="number" placeholder="8" value={formData.water_glasses}
              onChange={(e) => setFormData({...formData, water_glasses: e.target.value})} className="h-8 text-sm" data-testid="input-water" />
          </div>
          <div>
            <Label className="text-xs">Exercise (min)</Label>
            <Input type="number" placeholder="30" value={formData.exercise_minutes}
              onChange={(e) => setFormData({...formData, exercise_minutes: e.target.value})} className="h-8 text-sm" />
          </div>
        </div>
      </div>

      {/* Wellness */}
      <div className="space-y-3">
        <p className="text-xs font-medium text-gray-500 uppercase tracking-wider flex items-center gap-1">
          <Smile className="w-3 h-3" /> Wellness
        </p>
        <div>
          <Label className="text-xs">Mood</Label>
          <Select value={formData.mood} onValueChange={(v) => setFormData({...formData, mood: v})}>
            <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="How are you feeling?" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="poor">Poor</SelectItem>
              <SelectItem value="okay">Okay</SelectItem>
              <SelectItem value="good">Good</SelectItem>
              <SelectItem value="great">Great</SelectItem>
              <SelectItem value="excellent">Excellent</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Stress Level: {formData.stress_level}/10</Label>
          <Slider value={[formData.stress_level]} onValueChange={(v) => setFormData({...formData, stress_level: v[0]})} max={10} min={1} step={1} className="mt-2" />
        </div>
        <div>
          <Label className="text-xs">Energy Level: {formData.energy_level}/10</Label>
          <Slider value={[formData.energy_level]} onValueChange={(v) => setFormData({...formData, energy_level: v[0]})} max={10} min={1} step={1} className="mt-2" />
        </div>
      </div>

      {/* Notes */}
      <div>
        <Label className="text-xs">Notes (symptoms, feelings, etc.)</Label>
        <textarea placeholder="Any symptoms or notes about how you're feeling..."
          value={formData.notes} onChange={(e) => setFormData({...formData, notes: e.target.value})}
          className="w-full h-16 px-3 py-2 text-sm border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-[#166534]" />
      </div>

      {/* Submit */}
      <div className="flex gap-2 pt-2">
        <Button type="button" variant="outline" onClick={onClose} className="flex-1 h-9">Cancel</Button>
        <Button type="submit" disabled={isSubmitting} className="flex-1 h-9 bg-[#166534] hover:bg-[#14532d] text-white" data-testid="submit-health-form">
          {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Data'}
        </Button>
      </div>
    </form>
  );
};

const AnnaliseChat = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasGreeted, setHasGreeted] = useState(false);
  const [showPulse, setShowPulse] = useState(true);
  const [showHealthForm, setShowHealthForm] = useState(false);
  const messagesEndRef = useRef(null);
  const sessionId = useRef(getSessionId());

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => { scrollToBottom(); }, [messages]);

  // Listen for openHealthForm event from Header/Dashboard
  useEffect(() => {
    const handler = () => {
      setIsOpen(true);
      setIsMinimized(false);
      setShowHealthForm(true);
      setShowPulse(false);
    };
    window.addEventListener('openHealthForm', handler);
    return () => window.removeEventListener('openHealthForm', handler);
  }, []);

  // Auto-open chat after delay
  useEffect(() => {
    const hasSeenChat = sessionStorage.getItem('annalise_seen');
    if (!hasSeenChat) {
      const timer = setTimeout(() => {
        setIsOpen(true);
        sessionStorage.setItem('annalise_seen', 'true');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, []);

  // Greeting
  useEffect(() => {
    if (isOpen && !hasGreeted && messages.length === 0) {
      fetchGreeting();
    }
  }, [isOpen, hasGreeted, messages.length]);

  const fetchGreeting = async () => {
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/annalise/greeting`, {
        session_id: sessionId.current,
        is_new_visitor: isNewVisitor(),
        user_name: localStorage.getItem('user_name') || null
      });
      setMessages([{ id: Date.now(), type: 'bot', text: response.data.response }]);
      setHasGreeted(true);
    } catch (error) {
      setMessages([{ id: Date.now(), type: 'bot', text: "Hi there! I'm Annalise, your personal health assistant. Welcome to HealthChoices! How can I help you today?" }]);
      setHasGreeted(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isTyping) return;
    const userMessage = { id: Date.now(), type: 'user', text: inputValue };
    setMessages(prev => [...prev, userMessage]);
    const messageText = inputValue;
    setInputValue('');
    setIsTyping(true);
    setShowPulse(false);
    try {
      const response = await axios.post(`${API}/annalise/chat`, {
        session_id: sessionId.current,
        message: messageText,
        is_new_visitor: isNewVisitor(),
        user_name: localStorage.getItem('user_name') || null
      });
      setMessages(prev => [...prev, { id: Date.now() + 1, type: 'bot', text: response.data.response }]);
    } catch (error) {
      setMessages(prev => [...prev, { id: Date.now() + 1, type: 'bot', text: "I apologize, I'm having trouble connecting right now. Please try again in a moment!" }]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage(); }
  };

  const handleHealthDataSubmit = (data) => {
    setMessages(prev => [...prev, {
      id: Date.now(), type: 'bot',
      text: "Great job logging your health data! I've recorded your entries. Consistent tracking helps you and our AI doctors understand your health patterns better. Visit your Health Dashboard to see updated insights!"
    }]);
  };

  const quickQuestions = [
    "How do I get started?",
    "Log health data",
    "What AI doctors are available?",
    "How does tracking work?"
  ];

  const handleQuickQuestion = (question) => {
    if (question === "Log health data") {
      setShowHealthForm(true);
    } else {
      setInputValue(question);
      setTimeout(() => handleSendMessage(), 100);
    }
  };

  return (
    <>
      {/* Toggle Button */}
      <button
        onClick={() => { setIsOpen(!isOpen); setShowPulse(false); }}
        data-testid="annalise-toggle-btn"
        className={`fixed bottom-6 right-6 z-50 w-16 h-16 rounded-full shadow-2xl flex items-center justify-center transition-all duration-300 hover:scale-110 ${
          isOpen ? 'bg-gray-800 hover:bg-gray-700' : 'bg-gradient-to-br from-[#22c55e] to-[#166534] hover:shadow-[0_0_30px_rgba(34,197,94,0.4)]'
        }`}
      >
        {isOpen ? <X className="w-6 h-6 text-white" /> : (
          <>
            <MessageCircle className="w-7 h-7 text-white" />
            {showPulse && <span className="absolute top-0 right-0 w-4 h-4 bg-red-500 rounded-full animate-pulse border-2 border-white" />}
          </>
        )}
      </button>

      {/* Chat Window - Fixed positioning to be fully visible */}
      {isOpen && (
        <div
          data-testid="annalise-chat-window"
          className={`fixed z-50 bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden transition-all duration-300 ${
            isMinimized ? 'w-80 h-16 bottom-24 right-6' : 'w-96 bottom-24 right-6'
          }`}
          style={isMinimized ? {} : { height: 'min(550px, calc(100vh - 120px))' }}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-[#166534] to-[#22c55e] px-4 py-3 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-white font-semibold text-sm">Annalise</h3>
                <p className="text-green-100 text-xs">Your Personal Health Assistant</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => setIsMinimized(!isMinimized)}
                className="p-1.5 hover:bg-white/20 rounded-lg transition-colors">
                {isMinimized ? <Maximize2 className="w-4 h-4 text-white" /> : <Minimize2 className="w-4 h-4 text-white" />}
              </button>
              <button onClick={() => setIsOpen(false)}
                className="p-1.5 hover:bg-white/20 rounded-lg transition-colors" data-testid="annalise-close-btn">
                <X className="w-4 h-4 text-white" />
              </button>
            </div>
          </div>

          {!isMinimized && (
            <div className="flex flex-col" style={{ height: 'calc(100% - 56px)' }}>
              {showHealthForm ? (
                <div className="flex-1 overflow-y-auto">
                  <HealthDataForm onSubmit={handleHealthDataSubmit} onClose={() => setShowHealthForm(false)} />
                </div>
              ) : (
                <>
                  {/* Messages Area */}
                  <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50" data-testid="annalise-messages">
                    {isLoading ? (
                      <div className="flex items-center justify-center h-full">
                        <Loader2 className="w-8 h-8 animate-spin text-[#166534]" />
                      </div>
                    ) : (
                      <>
                        {messages.map((message) => (
                          <div key={message.id}
                            className={`flex items-start gap-2 ${message.type === 'user' ? 'flex-row-reverse' : ''}`}>
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                              message.type === 'user' ? 'bg-[#166534]' : 'bg-gradient-to-br from-[#22c55e] to-[#166534]'
                            }`}>
                              {message.type === 'user' ? <User className="w-4 h-4 text-white" /> : <Sparkles className="w-4 h-4 text-white" />}
                            </div>
                            <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 ${
                              message.type === 'user'
                                ? 'bg-[#166534] text-white rounded-tr-sm'
                                : 'bg-white text-gray-800 rounded-tl-sm shadow-sm border border-gray-100'
                            }`}>
                              <p className="text-sm leading-relaxed whitespace-pre-wrap">{message.text}</p>
                            </div>
                          </div>
                        ))}

                        {isTyping && (
                          <div className="flex items-start gap-2">
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#22c55e] to-[#166534] flex items-center justify-center">
                              <Sparkles className="w-4 h-4 text-white" />
                            </div>
                            <div className="bg-white rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm border border-gray-100">
                              <div className="flex gap-1">
                                <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                                <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                                <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                              </div>
                            </div>
                          </div>
                        )}

                        {messages.length <= 2 && !isTyping && (
                          <div className="pt-2">
                            <p className="text-xs text-gray-500 mb-2">Quick actions:</p>
                            <div className="flex flex-wrap gap-2">
                              {quickQuestions.map((q, i) => (
                                <button key={i} onClick={() => handleQuickQuestion(q)}
                                  className={`text-xs px-3 py-1.5 rounded-full transition-colors ${
                                    q === "Log health data"
                                      ? 'bg-[#166534] text-white hover:bg-[#14532d] flex items-center gap-1'
                                      : 'bg-white border border-gray-200 text-gray-700 hover:border-[#166534] hover:text-[#166534]'
                                  }`}>
                                  {q === "Log health data" && <PlusCircle className="w-3 h-3" />}
                                  {q}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                        <div ref={messagesEndRef} />
                      </>
                    )}
                  </div>

                  {/* Input Area */}
                  <div className="p-3 bg-white border-t border-gray-100 flex-shrink-0">
                    <button onClick={() => setShowHealthForm(true)}
                      className="w-full mb-2 py-2 px-3 bg-green-50 hover:bg-green-100 border border-green-200 rounded-xl text-sm text-[#166534] font-medium flex items-center justify-center gap-2 transition-colors"
                      data-testid="chat-log-health-btn">
                      <PlusCircle className="w-4 h-4" /> Log Health Data (No Wearables Needed)
                    </button>
                    <div className="flex gap-2">
                      <Input value={inputValue} onChange={(e) => setInputValue(e.target.value)}
                        onKeyPress={handleKeyPress} placeholder="Ask me anything..."
                        className="flex-1 text-sm rounded-xl border-gray-200 focus:border-[#166534] focus:ring-[#166534]"
                        disabled={isTyping} data-testid="annalise-input" />
                      <Button onClick={handleSendMessage} disabled={!inputValue.trim() || isTyping}
                        className="bg-[#166534] hover:bg-[#14532d] text-white rounded-xl px-4 disabled:opacity-50"
                        data-testid="annalise-send-btn">
                        <Send className="w-4 h-4" />
                      </Button>
                    </div>
                    <p className="text-[10px] text-gray-400 text-center mt-2">Powered by HealthChoices AI</p>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default AnnaliseChat;
